/**
 * Scale Theme V2 — Animation Controller
 *
 * Global scroll-triggered animation system.
 * Progressive enhancement: elements visible without JS,
 * hidden only when this script loads (.animations-ready on body).
 *
 * Architecture:
 * - Plain class (not a custom element — this is a service, not UI)
 * - Self-instantiates at module load
 * - Conditionally loaded in theme.liquid when animations_enabled
 *
 * 3-level cascade:
 * 1. Global preset (minimal/modern/dynamic/elegant/playful) → default animation type
 * 2. Section override (data-section-animate="off|fade-up|...") → overrides global
 * 3. Block (data-animate="inherit|none|fade|...") → inherits or overrides
 *
 * Features:
 * - IntersectionObserver with -15% rootMargin
 * - Visual stagger by viewport position (opt-in via data-stagger-children)
 * - TextSplitter for word/char animations
 * - MutationObserver for dynamic content (Section Rendering API)
 * - Shopify design mode (section:load/unload)
 * - prefers-reduced-motion
 * - will-change cleanup via animationend
 * - Slide clone exclusion
 * - Public API: Scale.animations.refresh() / .animate(el)
 */

// ── Preset → default animation type mapping ────────────

const PRESET_MAP = {
  minimal: 'fade',
  modern: 'fade-up',
  dynamic: 'fade-up',
  elegant: 'fade',
  playful: 'zoom-in',
};

// ── Text Splitter ──────────────────────────────────────

class TextSplitter {
  static #MAX_CHARS = 100;

  static splitWords(el) {
    const html = el.innerHTML.trim();
    if (!html) return;

    el.setAttribute('aria-label', el.textContent.trim());
    let i = 0;
    el.innerHTML = html.replace(/(<[^>]+>)|(&[^;]+;)|([^<&\s]+)/g, (m, tag, entity, word) => {
      if (tag) return tag;
      return `<span class="word"><span class="word-inner" style="--word-index:${i++}">${entity || word}</span></span>`;
    });
    el.dataset.textSplit = 'words';
  }

  static splitChars(el) {
    const text = el.textContent.trim();
    if (!text) return;
    if (text.length > this.#MAX_CHARS) return this.splitWords(el);

    el.setAttribute('aria-label', text);
    let i = 0;
    el.innerHTML = el.innerHTML.trim().replace(/(<[^>]+>)|(&[^;]+;)|([^<&])/g, (m, tag, entity, char) => {
      if (tag) return tag;
      if (char === ' ') return ' ';
      return `<span class="char" style="--char-index:${i++}">${entity || char}</span>`;
    });
    el.dataset.textSplit = 'chars';
  }

  static processAll() {
    document.querySelectorAll('[data-animate="text-words"]:not([data-text-split])').forEach(el => this.splitWords(el));
    document.querySelectorAll('[data-animate="text-chars"]:not([data-text-split])').forEach(el => this.splitChars(el));
  }
}

// ── Animation Controller ───────────────────────────────

class AnimationController {
  #controller = new AbortController();
  #observer;
  #mutation;
  #animated = new Set();
  #pendingDelays = new Map();
  #preset;
  #speed;

  constructor() {
    // Read global settings from Scale object (set in theme.liquid)
    this.#preset = window.Scale?.settings?.animations_preset || 'modern';
    this.#speed = window.Scale?.settings?.animations_speed || 'normal';

    if (this.#prefersReduced()) {
      this.#disable();
      this.#watchReducedMotion();
      return;
    }

    this.#watchReducedMotion();
    this.#init();
  }

  get signal() {
    return this.#controller.signal;
  }

  // ── Init ──────────────────────────────────────

  #init() {
    // Defer setup to idle time
    const setup = () => {
      // Mark body — CSS now hides [data-animate] elements
      document.body.classList.add('animations-ready');

      TextSplitter.processAll();
      this.#calculateStagger();
      this.#createObserver();
      this.#observeAll();
      this.#watchMutations();
      this.#watchDesignMode();
      this.#watchResize();
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(setup, { timeout: 150 });
    } else {
      setTimeout(setup, 0);
    }
  }

  // ── IntersectionObserver ──────────────────────

  #createObserver() {
    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !this.#animated.has(entry.target)) {
          this.#animate(entry.target);
        }
      }
    }, {
      rootMargin: '0px 0px -12% 0px',
      threshold: 0,
    });
  }

  #observeAll() {
    if (!this.#observer) return;

    const els = document.querySelectorAll('[data-animate]:not(.animated):not([data-animate="none"]):not([data-animate-manual])');
    for (const el of els) {
      // Skip hidden elements
      if (el.offsetParent === null) continue;
      // Skip slider clones
      if (el.closest('[data-clone], .slide-clone')) continue;
      this.#observer.observe(el);
    }
  }

  // ── Animate ───────────────────────────────────

  #animate(el) {
    if (this.#animated.has(el)) return;
    this.#animated.add(el);
    this.#observer?.unobserve(el);

    // Resolve animation type
    let type = el.getAttribute('data-animate');

    if (type === 'inherit') {
      type = this.#resolveInherit(el);
      if (type && type !== 'none' && type !== 'off') {
        el.setAttribute('data-animate', type);
      } else {
        // No animation — just show
        el.classList.add('animated');
        return;
      }
    }

    // Check if parent section disables animations
    const sectionAnim = el.closest('[data-section-animate]');
    if (sectionAnim?.dataset.sectionAnimate === 'off') {
      el.classList.add('animated');
      return;
    }

    // Get delay
    const delayStr = getComputedStyle(el).getPropertyValue('--animate-delay').trim();
    const delay = parseInt(delayStr, 10) || 0;

    if (delay > 0) {
      const timer = setTimeout(() => {
        this.#pendingDelays.delete(el);
        this.#trigger(el);
      }, delay);
      this.#pendingDelays.set(el, timer);
    } else {
      this.#trigger(el);
    }
  }

  #resolveInherit(el) {
    // 1. Check section-level override
    const section = el.closest('[data-section-animate]');
    if (section) {
      const sectionType = section.dataset.sectionAnimate;
      if (sectionType && sectionType !== 'global') return sectionType;
    }

    // 2. Fall back to global preset
    return PRESET_MAP[this.#preset] || 'fade-up';
  }

  #trigger(el) {
    requestAnimationFrame(() => {
      el.classList.add('animated');

      // Cleanup will-change after animation finishes
      el.addEventListener('animationend', () => {
        if (!el.hasAttribute('data-parallax')) {
          el.style.willChange = 'auto';
        }
      }, { once: true });

      // Fallback cleanup if animationend doesn't fire (e.g. no animation CSS)
      setTimeout(() => {
        el.style.willChange = 'auto';
      }, 2000);
    });
  }

  #cancelPending(el) {
    if (this.#pendingDelays.has(el)) {
      clearTimeout(this.#pendingDelays.get(el));
      this.#pendingDelays.delete(el);
    }
  }

  // ── Visual Stagger ────────────────────────────

  #calculateStagger() {
    // Only stagger children of containers that opt-in
    const containers = document.querySelectorAll('[data-stagger-children]');

    for (const container of containers) {
      const step = parseInt(container.dataset.staggerChildren, 10) || 100;
      const children = container.querySelectorAll(':scope > [data-animate]:not(.animated)');

      if (children.length < 2) continue;

      // Sort by visual position (top), not DOM order
      const sorted = [...children]
        .map(el => ({ el, top: el.getBoundingClientRect().top }))
        .sort((a, b) => a.top - b.top);

      sorted.forEach(({ el }, i) => {
        el.style.setProperty('--animate-delay', `${i * step}ms`);
      });
    }
  }

  // ── Mutation Observer ─────────────────────────

  #watchMutations() {
    let pending = false;

    this.#mutation = new MutationObserver((mutations) => {
      if (pending) return;

      let hasNew = false;
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType === 1 && (node.hasAttribute?.('data-animate') || node.querySelector?.('[data-animate]'))) {
            hasNew = true;
            break;
          }
        }
        if (hasNew) break;
      }
      if (!hasNew) return;

      pending = true;
      requestAnimationFrame(() => {
        TextSplitter.processAll();
        requestAnimationFrame(() => {
          pending = false;
          this.#calculateStagger();
          this.#observeAll();
        });
      });
    });

    this.#mutation.observe(document.body, { childList: true, subtree: true });
  }

  // ── Design Mode ───────────────────────────────

  #watchDesignMode() {
    if (!window.Shopify?.designMode) return;

    document.addEventListener('shopify:section:load', () => {
      setTimeout(() => {
        TextSplitter.processAll();
        this.#calculateStagger();
        this.#observeAll();
      }, 100);
    }, { signal: this.signal });

    document.addEventListener('shopify:section:unload', (e) => {
      const section = e.target;
      if (!section) return;
      section.querySelectorAll('[data-animate]').forEach(el => {
        this.#cancelPending(el);
        this.#observer?.unobserve(el);
        this.#animated.delete(el);
      });
    }, { signal: this.signal });
  }

  // ── Resize ────────────────────────────────────

  #watchResize() {
    let timer;
    window.addEventListener('resize', () => {
      clearTimeout(timer);
      timer = setTimeout(() => this.#calculateStagger(), 250);
    }, { signal: this.signal, passive: true });
  }

  // ── Reduced Motion ────────────────────────────

  #prefersReduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #watchReducedMotion() {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    mq.addEventListener('change', (e) => {
      if (e.matches) {
        this.#disable();
        this.#destroy();
      } else {
        this.#enable();
        this.#init();
      }
    }, { signal: this.signal });
  }

  #disable() {
    document.documentElement.classList.add('animations-disabled');
    document.body.classList.remove('animations-ready');
  }

  #enable() {
    document.documentElement.classList.remove('animations-disabled');
  }

  // ── Public API ────────────────────────────────

  refresh() {
    TextSplitter.processAll();
    this.#calculateStagger();
    setTimeout(() => this.#observeAll(), 50);
  }

  animate(target) {
    const els = typeof target === 'string'
      ? document.querySelectorAll(target)
      : target instanceof Element ? [target] : target;

    for (const el of els) {
      if (el?.hasAttribute('data-animate')) {
        this.#animate(el);
      }
    }
  }

  // ── Destroy ───────────────────────────────────

  #destroy() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#mutation?.disconnect();
    this.#mutation = null;
    this.#pendingDelays.forEach(t => clearTimeout(t));
    this.#pendingDelays.clear();
    this.#animated.clear();
    document.body.classList.remove('animations-ready');
  }
}

// ── Self-instantiate ───────────────────────────────────

const instance = new AnimationController();

// Expose public API
window.Scale = window.Scale || {};
window.Scale.animations = {
  refresh: () => instance.refresh(),
  animate: (target) => instance.animate(target),
};
